import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';

import { useAuth } from '../../auth/AuthContext.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { actionError } from '../../components/actionError.ts';
import { useFullScreenDialog } from '../../hooks/useFullScreenDialog.ts';
import { shopDetailPath } from '../../routing/routes.ts';
import { DEVICE_COUNT_FIELDS, DEVICE_COUNT_LABELS, validateDeviceCountField } from './deviceCountHelpers.ts';
import {
  buildCreateShopPayload,
  INITIAL_SHOP_CREATE_FORM,
  isShopCodeTaken,
  isShopNameTaken,
  validateShopCreateForm,
  type ShopCreateFormState,
} from './shopFormHelpers.ts';
import { SHOP_TYPE_OPTIONS } from './shopsHelpers.ts';

export interface ShopFormDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ShopFormDialog({ open, onClose }: Readonly<ShopFormDialogProps>): React.JSX.Element {
  const { api } = useAuth();
  const navigate = useNavigate();
  const fullScreen = useFullScreenDialog();

  const [form, setForm] = useState<ShopCreateFormState>(INITIAL_SHOP_CREATE_FORM);
  const [error, setError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [nameError, setNameError] = useState('');
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setForm(INITIAL_SHOP_CREATE_FORM);
      setError('');
      setCodeError('');
      setNameError('');
      setBusy(false);
      submittingRef.current = false;
    }
  }, [open]);

  const set = <K extends keyof ShopCreateFormState>(key: K, value: ShopCreateFormState[K]) => {
    setError('');
    setCodeError('');
    setNameError('');
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleClose = () => {
    onClose();
  };

  const submit = async () => {
    if (submittingRef.current) return;
    const problem = validateShopCreateForm(form);
    if (problem) {
      setError(problem);
      return;
    }
    submittingRef.current = true;
    setBusy(true);
    setError('');
    setCodeError('');
    setNameError('');
    try {
      const res = await api.shops.create(buildCreateShopPayload(form));
      navigate(shopDetailPath(res.shop_id));
    } catch (e) {
      if (isShopCodeTaken(e)) {
        setCodeError(e instanceof Error ? e.message : 'That shop code is already in use.');
      } else if (isShopNameTaken(e)) {
        setNameError(e instanceof Error ? e.message : 'That shop name is already in use.');
      } else {
        setError(actionError(e));
      }
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : handleClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>Add shop</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 0.5 }}>
          <TextField
            label="Shop code"
            value={form.shop_code}
            onChange={(e) => set('shop_code', e.target.value)}
            fullWidth
            disabled={busy}
            error={codeError !== ''}
            helperText={codeError || '1-8 characters. Cannot be changed once set.'}
            slotProps={{ htmlInput: { 'aria-label': 'shop-code', maxLength: 8 } }}
          />
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            fullWidth
            disabled={busy}
            error={nameError !== ''}
            helperText={nameError || undefined}
            slotProps={{ htmlInput: { 'aria-label': 'shop-name' } }}
          />
          <FormControl fullWidth disabled={busy}>
            <InputLabel id="shop-type-label">Type</InputLabel>
            <Select
              labelId="shop-type-label"
              label="Type"
              value={form.shop_type}
              onChange={(e) => set('shop_type', e.target.value)}
              inputProps={{ 'aria-label': 'shop-type' }}
            >
              {SHOP_TYPE_OPTIONS.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Email (optional)"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            fullWidth
            disabled={busy}
            slotProps={{ htmlInput: { 'aria-label': 'shop-email' } }}
          />
          <TextField
            label="Phone (optional)"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
            fullWidth
            disabled={busy}
            slotProps={{ htmlInput: { 'aria-label': 'shop-phone' } }}
          />
          <TextField
            label="Address (optional)"
            value={form.address}
            onChange={(e) => set('address', e.target.value)}
            fullWidth
            disabled={busy}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { 'aria-label': 'shop-address' } }}
            sx={{ gridColumn: '1 / -1' }}
          />
          {DEVICE_COUNT_FIELDS.map((field) => (
            <TextField
              key={field}
              label={DEVICE_COUNT_LABELS[field]}
              value={form[field]}
              onChange={(e) => set(field, e.target.value)}
              fullWidth
              disabled={busy}
              error={validateDeviceCountField(field, form[field]) !== null}
              helperText={validateDeviceCountField(field, form[field]) ?? '0-1000, default 0.'}
              slotProps={{ htmlInput: { 'aria-label': field, inputMode: 'numeric' } }}
            />
          ))}
          {error && (
            <Alert role="alert" severity="error" sx={{ fontWeight: 700, gridColumn: '1 / -1' }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <QuietButton onClick={handleClose} disabled={busy}>
          Cancel
        </QuietButton>
        <Button onClick={() => void submit()} disabled={busy}>
          Create shop
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ShopFormDialog;
